import { ArrowLeft, Newspaper } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AgentHandoff } from '../../components/jobs/AgentHandoff';
import { Modal, useToast } from '../../components/ui/overlays';
import { Button, ErrorBlock, Field, Input, LoadingBlock, Select, Switch, Textarea } from '../../components/ui/primitives';
import { useContentChannels, useCreateContentBatch, useTopicStatusCounts } from '../../hooks/queries';

/**
 * Queues content generation for a selected set of topics: creates a batch, one task per topic
 * and a CONTENT job. From there an MCP agent drafts the posts. Passing no topics queues every
 * PENDING/SKIPPED topic in the project.
 */
interface QueueContentProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  topicIds: string[];
  onQueued?: () => void;
  onBack?: () => void;
}

export const QueueContentModal = (props: QueueContentProps) => props.open ? <QueueContentForm {...props} /> : null;

const QueueContentForm = ({
  open,
  onClose,
  projectId,
  topicIds,
  onQueued,
  onBack,
}: QueueContentProps) => {
  const createBatch = useCreateContentBatch(projectId);
  const { data: channels, isLoading: channelsLoading, error: channelsError } = useContentChannels(projectId);
  const { data: counts, isLoading: countsLoading, error: countsError } = useTopicStatusCounts(projectId);
  const toast = useToast();
  const [queuedCount, setQueuedCount] = useState<number | null>(null);
  const activeChannels = (channels ?? []).filter((channel) => channel.isActive);
  const backlogCount = (counts?.PENDING ?? 0) + (counts?.SKIPPED ?? 0);
  const topicCount = topicIds.length || Math.min(200, backlogCount);
  const loading = channelsLoading || countsLoading;
  const error = channelsError || countsError;

  const [form, setForm] = useState({
    name: `Content ${new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`,
    channelId: '',
    instructions: '',
    language: '',
    requireApproval: true,
    minSeoScore: '',
  });

  const submit = async () => {
    if (createBatch.isPending) return;
    if (!topicCount || !activeChannels.length) return;
    if (form.name.trim().length < 2 || form.name.trim().length > 120) {
      toast.error('Use a batch name between 2 and 120 characters');
      return;
    }
    if (form.minSeoScore !== '' && (!Number.isInteger(Number(form.minSeoScore)) || Number(form.minSeoScore) < 0 || Number(form.minSeoScore) > 100)) {
      toast.error('Minimum SEO score must be a whole number from 0 to 100');
      return;
    }
    try {
      const batch = await createBatch.mutateAsync({
        name: form.name.trim() || 'Content',
        channelId: form.channelId || undefined,
        instructions: form.instructions || undefined,
        language: form.language || undefined,
        requireApproval: form.requireApproval,
        minSeoScore: form.minSeoScore === '' ? undefined : Number(form.minSeoScore),
        topicIds: topicIds.length ? topicIds : undefined,
      });
      setQueuedCount(batch.taskCount ?? topicCount);
      onQueued?.();
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!createBatch.isPending) onClose(); }}
      title={queuedCount !== null ? 'Content job ready' : 'Create posts'}
      description={
        queuedCount !== null ? 'Your content brief is queued. Start your connected agent to write the posts.' : topicIds.length
          ? `${topicIds.length} topic${topicIds.length === 1 ? '' : 's'} selected. An agent will draft one post per topic.`
          : 'Turn your topic backlog into posts with a content agent.'
      }
      footer={
        queuedCount !== null ? <Button onClick={onClose}>Done</Button> : <>
          <Button variant="ghost" disabled={createBatch.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={createBatch.isPending}
            disabled={loading || Boolean(error) || !topicCount || !activeChannels.length || topicIds.length > 500 || form.name.trim().length < 2}
            icon={<Newspaper className="h-4 w-4" />}
          >
            Queue {topicCount || ''} {topicCount === 1 ? 'post' : 'posts'}
          </Button>
        </>
      }
    >
      {queuedCount !== null ? <AgentHandoff projectId={projectId} type="CONTENT" summary={`${queuedCount} ${queuedCount === 1 ? 'topic' : 'topics'} queued for drafting`} onClose={onClose} /> : loading ? <LoadingBlock /> : error ? <ErrorBlock error={error} /> : <fieldset disabled={createBatch.isPending} className="space-y-4">
        {onBack ? <Button variant="ghost" size="sm" disabled={createBatch.isPending} icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack}>Agent types</Button> : null}
        <div className="rounded-lg border border-line bg-raised p-4 text-[13px]">
          <p className="font-semibold text-ink">{topicCount} {topicIds.length ? 'selected' : 'pending or skipped'} {topicCount === 1 ? 'topic' : 'topics'}</p>
          <p className="mt-1 text-muted">{topicCount ? 'One post per eligible topic. Topics already queued or drafted are skipped.' : 'Add topics to give the agent something to write about.'}</p>
          {!topicIds.length && backlogCount > 200 ? <p className="mt-1 text-muted">The next 200 of {backlogCount} topics will be queued, ordered by priority and age.</p> : null}
          <Link to={`/projects/${projectId}/topics`} onClick={onClose} className="mt-2 inline-block text-brand hover:underline">{topicCount ? 'Choose specific topics' : 'Add topics'}</Link>
        </div>
        {!activeChannels.length ? <p className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-[13px] text-muted">Set up an active publishing channel before queueing posts. <Link className="text-brand hover:underline" to={`/projects/${projectId}/content-channel`} onClick={onClose}>Set up publishing</Link></p> : null}
        {topicIds.length > 500 ? <p className="text-sm text-danger">Select up to 500 topics per batch.</p> : null}
        <Field label="Batch name" htmlFor="batch-name">
          <Input
            id="batch-name"
            maxLength={120}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>

        <Field
          label="Extra instructions"
          htmlFor="batch-instructions"
          hint="Added on top of the project content prompt. Use it for the angle of this specific batch."
        >
          <Textarea
            id="batch-instructions"
            rows={4}
            maxLength={8000}
            className="prompt-input"
            value={form.instructions}
            placeholder="Lean into the how-to angle. Reference the spring pricing page. Keep each post under 900 words."
            onChange={(event) => setForm({ ...form, instructions: event.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Publish to"
            htmlFor="batch-channel"
            hint={
              channels?.length
                ? 'Manage these under Publishing.'
                : 'None configured yet — add one under Publishing.'
            }
          >
            <Select
              id="batch-channel"
              value={form.channelId}
              onChange={(event) => setForm({ ...form, channelId: event.target.value })}
            >
              <option value="">Project default</option>
              {activeChannels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.label}
                  {channel.isDefault ? ' · default' : ''}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Language"
            htmlFor="batch-language"
            hint="Leave empty to use each topic's own language."
          >
            <Input
              id="batch-language"
              maxLength={12}
              value={form.language}
              placeholder="en"
              onChange={(event) => setForm({ ...form, language: event.target.value })}
            />
          </Field>

          <Field
            label="Minimum SEO score"
            htmlFor="batch-seo"
            hint="Posts below this are held for review even without approval."
          >
            <Input
              id="batch-seo"
              type="number"
              min={0}
              max={100}
              value={form.minSeoScore}
              placeholder="Channel default"
              onChange={(event) => setForm({ ...form, minSeoScore: event.target.value })}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-line bg-raised px-4 py-3">
          <Switch
            checked={form.requireApproval}
            onChange={(value) => setForm({ ...form, requireApproval: value })}
            label="Review each post before it publishes"
            description={
              form.requireApproval
                ? 'Posts land in Posts → Drafts and wait for you to approve them.'
                : 'Posts are scheduled automatically as soon as the agent writes them.'
            }
          />
        </div>
      </fieldset>}
    </Modal>
  );
};
